import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProductionOrder } from '@ecinsa/shared';
import { ApiError, api } from '../lib/api';
import { useTerminal } from '../lib/store';
import { TopBar } from '../components/TopBar';
import { OrderPicker, PhasePicker } from '../components/pickers';
import { Empty } from '../components/ui';

export function StartPhase() {
  const { catalog, startPhase, notify, busy } = useTerminal();
  const navigate = useNavigate();
  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [phases, setPhases] = useState<{ code: string; enabled: boolean; reason: string | null }[]>([]);
  const [loading, setLoading] = useState(false);

  const phaseNames = useMemo(
    () => new Map((catalog?.phases ?? []).map((p) => [p.code, p.name])),
    [catalog],
  );
  const manualEntry = catalog?.parameters['terminal.manualEntry'] === 'true';

  useEffect(() => {
    if (!order) return;
    setLoading(true);
    api
      .order(order.docEntry)
      .then((res) => setPhases(res.phases))
      // Sin red se ofrecen todas las fases de la OF cacheada; el servidor
      // volverá a validar los permisos cuando se envíe la acción.
      .catch(() => setPhases(order.phases.map((code) => ({ code, enabled: true, reason: null }))))
      .finally(() => setLoading(false));
  }, [order]);

  const confirm = async (phaseCode: string) => {
    if (!order) return;
    try {
      await startPhase({ orderDocEntry: order.docEntry, phaseCode, project: order.project });
      notify({ kind: 'ok', title: 'Fase iniciada', body: `OF ${order.docNum} · ${phaseNames.get(phaseCode) ?? phaseCode}` });
      navigate('/');
    } catch (err) {
      notify({ kind: 'error', title: 'No se pudo iniciar la fase', body: err instanceof ApiError ? err.message : String(err) });
    }
  };

  return (
    <div className="app">
      <TopBar
        title={order ? `OF ${order.docNum} · elige la fase` : 'Inicio de fase · elige la OF'}
        subtitle={order ? order.itemDescription : 'Solo se muestran las OFs liberadas en las que puedes imputar'}
        onBack={() => (order ? setOrder(null) : navigate('/'))}
      />
      <div className="content">
        {!order ? (
          (catalog?.orders.length ?? 0) === 0 ? (
            <Empty icon="📋" title="No hay órdenes liberadas" body="Libera una OF en SAP para poder imputar tiempos." />
          ) : (
            <OrderPicker orders={catalog?.orders ?? []} manualEntry={manualEntry} onPick={setOrder} />
          )
        ) : loading || busy ? (
          <Empty icon="⏳" title="Cargando fases…" />
        ) : (
          <PhasePicker phases={phases} names={phaseNames} onPick={(code) => void confirm(code)} />
        )}
      </div>
    </div>
  );
}
